import { logger } from 'common/services/logger';
import { assert, generateDynamicPath } from 'common/utils';
import { communityActions } from 'community/api/actions';
import { invalidateMyCommunitiesEffect } from 'community/api/effects';
import { selectMyCommunitiesIds, selectMyCommunity } from 'community/api/selectors';
import i18n from 'i18n';
import { addAppErrorMessage, addAppInfoMessage } from 'modules/alerts/state/actions';
import { natalieFeedbackApi } from 'natalie/api/feedbackApi';
import { natalieEventApi } from 'natalie/api/natalieEventApi';
import { selectCurrentNatalieEvent } from 'natalie/api/natalieEventApi.selectors';
import { NATALIE_REQUEST_EVENT_ID_KEY, NATALIE_VERSION } from 'natalie/constants';
import { isCreateEventRequestFlow } from 'natalie/utils/isCreateEventRequestFlow';
import { buildNataliePath, buildPostRegistrationPath } from 'pages/Natalie/navigation';
import { NatalieRoutes, getAccountlessLandingUrl, getNataliePublicPath } from 'pages/Natalie/routes';
import { push } from 'redux-first-history';
import { selectUserAccount } from 'user/api/selectors';

import type { natalieActions } from './natalie.actions';
import type { RegisterToNatalieEventArgs, RequestNatalieCreateEventResponse } from 'natalie/api/natalieEvent.types';
import type { PostNatalieFeedbackPayload } from 'natalie/api/types';
import type { Effect } from 'store';

export const startChattingButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.detailsPage.startChattingButton.clicked>
> = ({ payload: { id } }, { dispatch }) => {
  dispatch(natalieEventApi.endpoints.registerToNatalieEvent.initiate({ id }));
};

export const joinButtonClickedEffect: Effect = ({ payload: { id } }, { dispatch, getState }) => {
  try {
    const state = getState();
    // Resolved from the click's own event id rather than the current pathname, for the same
    // reason `navigateToJoin` below is: the path-shaped lookup matches participant routes
    // only, and the intake requester starts the conversation from the merged intake screen at
    // `/natalie-admin/create/voice`, which carries no event id for it to match. Read off the
    // path, that found nothing, the assert threw, the catch logged, and the control silently
    // did nothing.
    const natalieEvent = natalieEventApi.endpoints.getNatalieEvent.select(id)(state).data;

    assert(!!natalieEvent, 'Natalie event is not present');

    const hasVideo = !!natalieEvent.videoUrl;
    const searchParams = window.location.search;
    const urlParams = new URLSearchParams(searchParams);
    const wantsConfigure = urlParams.get('configure') === 'true';

    const roomRoute = wantsConfigure ? NatalieRoutes.Configure : NatalieRoutes.RoomLiveKit;
    const route = hasVideo ? NatalieRoutes.WaitingRoom : roomRoute;

    // Preserve query params for the navigation
    const path = generateDynamicPath(route, { id }) + searchParams;
    dispatch(push(path, { allow: true }));
  } catch (error) {
    logger.error(error);
  }
};

export const copyPublicLinkButtonClickedEffect: Effect = async ({ payload }, { dispatch }) => {
  try {
    const url = getNataliePublicPath(payload);

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

export const copyPublicTestLinkButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.list.moreActions.copyPublicTestLinkButtonClicked>
> = async ({ payload: { eventId, testInviteCode } }, { dispatch }) => {
  try {
    // Registered test link: /natalie/{eventId}/public?testInviteCode={code}
    const url = buildNataliePath(NatalieRoutes.Public, { id: eventId }, '', { testInviteCode }, { isAbsolute: true });

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

export const copyAccountlessLinkButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.list.moreActions.copyAccountlessLinkButtonClicked>
> = async ({ payload: { eventId, inviteCode } }, { dispatch }) => {
  try {
    const url = getAccountlessLandingUrl(eventId, inviteCode);

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

export const copyAccountlessTestLinkButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.list.moreActions.copyAccountlessTestLinkButtonClicked>
> = async ({ payload: { eventId, inviteCode, testInviteCode } }, { dispatch }) => {
  try {
    // Guest test link: /natalie/{eventId}/{inviteCode}?testInviteCode={code} —
    // real code in the path, test code in the query (server resolves test).
    const url = buildNataliePath(
      NatalieRoutes.AccountlessLanding,
      { eventId, inviteCode },
      '',
      { testInviteCode },
      { isAbsolute: true }
    );

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

const updateMyCommunities: Effect = async ({ meta }, { dispatch, getState }) => {
  const state = getState();
  const args = meta.arg.originalArgs as RegisterToNatalieEventArgs;

  const myCommunitiesIds = selectMyCommunitiesIds(state);
  const { communityId } = await dispatch(natalieEventApi.endpoints.getNatalieEvent.initiate(args.id)).unwrap();

  if (communityId !== undefined && !myCommunitiesIds?.includes(communityId)) {
    // user was not in open natter community before, but submitting availability means they were added to the community
    // so we want to refresh community related data
    dispatch(communityActions.community.registeredToCommunity({ communityId }));
  }
};

const navigateToJoin: Effect = ({ meta }, { dispatch, getState }) => {
  const args = meta.arg.originalArgs as RegisterToNatalieEventArgs;

  // Preserve query params (including provider) through navigation
  const searchParams = window.location.search;

  const state = getState();
  // Resolved from the register action's own argument rather than the current pathname. The
  // path-based lookup only ever worked because every caller already happened to be on that
  // event's page; the intake flow registers from `/natalie-admin/create/voice`, which carries
  // no event id, and adding that route to the path matcher could not help — there is nothing
  // in it to match.
  const natalieEvent = natalieEventApi.endpoints.getNatalieEvent.select(args.id)(state).data;

  dispatch(push(buildPostRegistrationPath(args.id, natalieEvent?.communityId, searchParams), { allow: true }));
};

export const registerToNatalieEventSuccessEffect: Effect = async (action, api) => {
  const args = action.meta.arg.originalArgs as RegisterToNatalieEventArgs;

  if (args.silent) {
    // Silent registration: only update community membership, no navigation (fire-and-forget).
    // Two callers. The magic-link flow auto-registers a user who never asked to go anywhere.
    // The intake screen registers a requester who is already on the screen that enters the
    // conversation itself — both participant destinations are wrong for them, and pushing
    // either would bounce them off the screen they are already on. Discriminating here on the
    // caller's own intent rather than on the event id is deliberate: an id-keyed check would
    // also silence the Details page's "Start chatting" for that same event, for the rest of
    // the tab session, because the request-flow id outlives the intake screen.
    try {
      await updateMyCommunities(action, api);
    } catch (error: unknown) {
      logger.error(error);
    }
  } else {
    // Invalidate communities cache (fire-and-forget, doesn't need to block navigation)
    invalidateMyCommunitiesEffect(action, api);
    // Wait for community switch to complete before navigating — the join page
    // and member setup dialog need the active community to be set correctly
    try {
      await updateMyCommunities(action, api);
    } catch (error: unknown) {
      logger.error(error);
    }
    navigateToJoin(action, api);
  }
};

export const requestNatalieCreateEventSuccessEffect: Effect = (action) => {
  const { eventId } = action.payload as RequestNatalieCreateEventResponse;

  // Written here, when the event comes into existence on start, rather than on arrival at the
  // intake screen: someone who reads the briefing and leaves has provisioned nothing, so no
  // screen treats them as mid-intake.
  //
  // Written synchronously, while the fulfilled action is still being dispatched, so
  // `isCreateEventRequestFlow` already recognises the event by the time the screen that
  // started the chain registers and the navigation effect has to tell an intake requester from
  // a participant.
  //
  // Registration is NOT dispatched from here: the intake screen awaits the chain itself,
  // because it has to land the requester in the conversation carrying the device choices its
  // own form holds, and only it knows when the chain failed and start can be pressed again.
  try {
    sessionStorage.setItem(NATALIE_REQUEST_EVENT_ID_KEY, eventId);
  } catch (error) {
    logger.error(error);
  }
};

export const requestNatalieCreateEventFailedEffect: Effect = (_action, { dispatch }) => {
  dispatch(addAppErrorMessage(i18n.t('natalie:list.requestFailed')));
};

export const registerToNatalieEventFailedEffect: Effect = (action, { dispatch }) => {
  const args = action.meta.arg.originalArgs as RegisterToNatalieEventArgs;

  // Scoped to the intake flow. Everywhere else registration is initiated from a screen the
  // user can stay on and retry from; the requester is the one left stranded, because the
  // success effect was their only route off the priming page.
  if (!isCreateEventRequestFlow(args.id)) {
    return;
  }

  dispatch(addAppErrorMessage(i18n.t('natalie:list.requestFailed')));

  // The request itself succeeded, so the event id is already stashed, but the requester never
  // reached the conversation. Clearing it puts them back to being an ordinary visitor to that
  // event rather than one mid-intake.
  try {
    sessionStorage.removeItem(NATALIE_REQUEST_EVENT_ID_KEY);
  } catch (error) {
    logger.error(error);
  }
};

export const beforeJoinContinueButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.beforeJoinPage.continueButton.clicked>
> = ({ payload: { id } }, { dispatch }) => {
  const searchParams = window.location.search;
  dispatch(push(generateDynamicPath(NatalieRoutes.Join, { id }) + searchParams, { allow: true }));
};

export const natalieIssueReportedEffect: Effect = ({ payload: { type, description } }, { dispatch, getState }) => {
  const state = getState();
  const user = selectUserAccount(state);
  const community = selectMyCommunity(state);
  const natalieEvent = selectCurrentNatalieEvent(state);

  try {
    assert(!!user, 'User is not present');
    assert(!!community, 'Community is not present');

    const { id: userId, firstName, lastName, email } = user;
    const { id: communityId, name } = community;

    const payload: PostNatalieFeedbackPayload = {
      type,
      description,
      user: {
        id: userId,
        name: `${firstName} ${lastName}`,
        email,
      },
      community: {
        id: communityId,
        name,
      },
      event: natalieEvent ? { id: natalieEvent.id, name: natalieEvent.name } : null,
      agoraChannel: '',
      natalieVersion: NATALIE_VERSION,
      timestamp: new Date().toISOString(),
    };

    dispatch(natalieFeedbackApi.endpoints.postFeedback.initiate(payload, { track: false }));
  } catch (error) {
    logger.error(error);
    dispatch(addAppErrorMessage(i18n.t('natalie:feedback.failedToReportAnIssue')));
  }
};
